import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Table,
  Modal,
  Input,
  Switch
} from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { useRecoilValue } from 'recoil';
import {
  businessAtom,
  timezoneAtom
} from '../../../atoms/Atoms';
import api from '../../../api/api';
import { renderDate } from '../../utils/functions';

function SelfCheckout() {
  const { t } = useTranslation();
  const history = useHistory();
  const timezone = useRecoilValue(timezoneAtom);
  const business = useRecoilValue(businessAtom);
  const [loading, setLoading] = useState(false);
  const [selfCheckoutLanes, setSelfCheckoutLanes] = useState([]);
  const [selectedSelfCheckoutLane, setSelectedSelfCheckoutLane] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  if (!business) {
    history.push('/');
    return null;
  }

  function getSelfCheckoutLanes() {
    setLoading(true);
    api
      .get(`selfcheckout/sclanes/${business?.id}`)
      .then((response) => {
        setLoading(false);
        setSelfCheckoutLanes(response.data.data.results);
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }

  function create() {
    setLoading(true);
    api
      .post('selfcheckout/create', {
        ...selectedSelfCheckoutLane,
        businessId: business?.id
      })
      .then((response) => {
        setLoading(false);
        setCreateNew(false);
        getSelfCheckoutLanes();
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }

  function update() {
    setLoading(true);
    api
      .post('selfcheckout/update', selectedSelfCheckoutLane)
      .then((response) => {
        setLoading(false);
        setUpdateExisting(false);
        getSelfCheckoutLanes();
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }

  function remove() {
    setLoading(true);
    api
      .post(`selfcheckout/delete/${selectedSelfCheckoutLane?.id}`)
      .then((response) => {
        setLoading(false);
        setUpdateExisting(false);
        getSelfCheckoutLanes();
      })
      .catch((error) => {
        setLoading(false);
        console.error(error);
      });
  }

  useEffect(() => {
    getSelfCheckoutLanes();
  }, [business]);

  useEffect(() => {
    getSelfCheckoutLanes();
  }, []);

  useEffect(() => {
    if (createNew) {
      setSelectedSelfCheckoutLane({
        tag: '',
        number: '',
        isKiosk: false
      });
    }
  }, [createNew]);

  return (
    <>
      <PageHeader
        className="mb-4 px-0"
        title={t('self_checkout_lanes')}
        extra={[
          <Button
            id="reload"
            type="primary"
            key="reload"
            size="small"
            onClick={() => getSelfCheckoutLanes()}
          >
            {t('reload')}
          </Button>,
          <Button
            type="primary"
            key="create"
            size="small"
            onClick={() => {
              setCreateNew(true);
            }}
          >
            {t('create_new')}
          </Button>
        ]}
      />
      <Table
        pagination={false}
        size="small"
        bordered
        rowKey="id"
        loading={loading}
        columns={[
          {
            title: t('lane'),
            key: 'lane',
            align: 'center',
            dataIndex: 'lane',
            className: 'text-xs',
            render: (row) => (
              <span>{row}</span>
            ),
          },
          {
            title: t('tag'),
            key: 'tag',
            align: 'center',
            dataIndex: 'tag',
            className: 'text-xs',
            render: (row) => (
              <span>{row}</span>
            ),
          },
          {
            title: t('is_kiosk'),
            key: 'iskiosk',
            align: 'center',
            dataIndex: 'isKiosk',
            className: 'text-xs',
            render: (row) => (
              <span>{row ? t('yes') : t('no')}</span>
            ),
          },
          {
            title: t('version'),
            key: 'lastReportedVersion',
            align: 'center',
            dataIndex: 'lastReportedVersion',
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('device_manufacturer'),
            key: 'lastReportedDeviceManufacturer',
            align: 'center',
            dataIndex: 'lastReportedDeviceManufacturer',
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('device_model'),
            key: 'lastReportedDeviceModel',
            align: 'center',
            dataIndex: 'lastReportedDeviceModel',
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('device_name'),
            key: 'lastReportedDeviceName',
            align: 'center',
            dataIndex: 'lastReportedDeviceName',
            className: 'text-xs',
            render: (row) => (
              <span>{row ?? '-'}</span>
            ),
          },
          {
            title: t('last_transaction_date'),
            key: 'lastTransactionOn',
            align: 'center',
            dataIndex: 'lastTransactionOn',
            className: 'text-xs',
            render: (row) => (
              <span>{row ? renderDate(row, timezone) : '-'}</span>
            ),
          },
          {
            title: t('edit'),
            key: 'edit',
            align: 'center',
            className: 'text-xs',
            render: (text, record) => (
              <Button
                size="small"
                onClick={() => {
                  setSelectedSelfCheckoutLane(record);
                  setUpdateExisting(true);
                }}
              >
                {t('edit')}
              </Button>
            ),
          },
        ]}
        dataSource={selfCheckoutLanes}
      />
      <Modal
        closable="true"
        centered="true"
        open={createNew}
        onCancel={() => setCreateNew(false)}
        footer={[
          <Button
            key="createButton"
            disabled={loading}
            type="primary"
            onClick={() => create()}
          >
            {t('create')}
          </Button>,
          <Button
            key="back"
            disabled={loading}
            onClick={() => setCreateNew(false)}
          >
            {t('close')}
          </Button>
        ]}
      >
        <PageHeader
          title={t('create_self_checkout_lane')}
        />
        <div className="mb-4">
          <strong>{t('lane')}</strong>
          <Input
            disabled={loading}
            className="w-full"
            value={selectedSelfCheckoutLane?.number}
            onChange={(obj) => {
              setSelectedSelfCheckoutLane({
                ...selectedSelfCheckoutLane,
                number: obj.target.value
              });
            }}
          />
        </div>
        <div className="mb-4">
          <strong>{t('tag')}</strong>
          <Input
            disabled={loading}
            className="w-full"
            value={selectedSelfCheckoutLane?.tag}
            onChange={(obj) => {
              setSelectedSelfCheckoutLane({
                ...selectedSelfCheckoutLane,
                tag: obj.target.value
              });
            }}
          />
        </div>
        <div className="mb-4">
          <strong>{t('is_kiosk')}</strong>
          <Switch
            disabled={loading}
            style={{ float: 'right' }}
            checked={selectedSelfCheckoutLane?.isKiosk}
            onChange={(checked) => {
              setSelectedSelfCheckoutLane({
                ...selectedSelfCheckoutLane,
                isKiosk: checked
              });
            }}
          />
        </div>
      </Modal>
      <Modal
        closable="true"
        centered="true"
        open={updateExisting}
        onCancel={() => setUpdateExisting(false)}
        footer={[
          <Button
            key="updateButton"
            type="primary"
            disabled={loading}
            onClick={() => update()}
          >
            {t('update')}
          </Button>,
          <Button
            key="removeButton"
            disabled={loading}
            type="primary"
            danger
            onClick={() => remove()}
          >
            {t('remove')}
          </Button>,
          <Button
            key="back"
            disabled={loading}
            onClick={() => setUpdateExisting(false)}
          >
            {t('close')}
          </Button>
        ]}
      >
        <PageHeader
          title={t('update_self_checkout_lane')}
        />
        <div className="mb-4">
          <strong>{t('lane')}</strong>
          <Input
            disabled={loading}
            className="w-full"
            value={selectedSelfCheckoutLane?.lane}
            onChange={(obj) => {
              setSelectedSelfCheckoutLane({
                ...selectedSelfCheckoutLane,
                lane: obj.target.value
              });
            }}
          />
        </div>
        <div className="mb-4">
          <strong>{t('tag')}</strong>
          <Input
            disabled={loading}
            className="w-full"
            value={selectedSelfCheckoutLane?.tag}
            onChange={(obj) => {
              setSelectedSelfCheckoutLane({
                ...selectedSelfCheckoutLane,
                tag: obj.target.value
              });
            }}
          />
        </div>
        <div className="mb-4">
          <strong>{t('is_kiosk')}</strong>
          <Switch
            disabled={loading}
            style={{ float: 'right' }}
            checked={selectedSelfCheckoutLane?.isKiosk}
            onChange={(checked) => {
              setSelectedSelfCheckoutLane({
                ...selectedSelfCheckoutLane,
                isKiosk: checked
              });
            }}
          />
        </div>
      </Modal>
    </>
  );
}
export default SelfCheckout;
